import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { snakeToCamelCase, camelToSnakeCase } from './utils'

// Module augmentation (not a local `as` cast, since this flag must be both
// SET by a caller's config literal and READ inside the request interceptor
// below — an inline cast at one site wouldn't type-check at the other).
declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Opt this one request OUT of the request interceptor's automatic
     * camelCase -> snake_case body transform (see below) — for a body
     * containing data-level dictionary keys that are arbitrary values (e.g.
     * an id-keyed map), not field names, and so must reach the wire
     * unchanged. The caller is then responsible for building an
     * already-wire-shaped body (harmless for any key that's genuinely a
     * plain lowercase field name, since the transform is a no-op on those
     * anyway).
     */
    skipCaseTransform?: boolean
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'

/**
 * Token refresh callback - set by AuthContext
 */
let tokenRefreshCallback: (() => Promise<string | null>) | null = null

/**
 * Register token refresh callback from AuthContext
 */
export function setTokenRefreshCallback(callback: (() => Promise<string | null>) | null): void {
  tokenRefreshCallback = callback
}

/**
 * Axios instance configured for SmartExtract API
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutes
})

// Request interceptor to transform camelCase to snake_case
// Skip transformation for FormData (file uploads)
apiClient.interceptors.request.use((config) => {
  if (config.data && typeof config.data === 'object') {
    // Don't transform FormData - it breaks file uploads
    if (!(config.data instanceof FormData)) {
      // camelToSnakeCase recurses into EVERY nested object's keys, including
      // data-level dictionary keys that aren't field names at all (e.g. a
      // `{[roll]: version}` map keyed by an arbitrary roll number) — the
      // request-side mirror of the "key transformation gotcha" documented in
      // frontend/CLAUDE.md for responses. A roll containing an uppercase
      // letter (backend's ID_PATTERN explicitly allows `[a-zA-Z0-9_-]`, e.g.
      // "R1") would otherwise get its dict key silently mangled
      // ("R1" -> "_r1"), and the backend's `dict.get(roll, ...)` lookup would
      // miss. `skipCaseTransform` (same escape-hatch shape as this
      // interceptor's own `_retry` config flag below) opts a single request
      // OUT of the transform so its caller can pre-build an
      // already-wire-shaped body; every other call site is unaffected.
      if (!config.skipCaseTransform) {
        config.data = camelToSnakeCase(config.data)
      }
    } else {
      // For FormData, let Axios set the correct Content-Type with boundary
      delete config.headers['Content-Type']
    }
  }
  return config
})

// Track if we're currently refreshing to avoid infinite loops
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: Error) => void
}> = []

function processQueue(error: Error | null, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Response interceptor to transform snake_case to camelCase and handle 401
apiClient.interceptors.response.use(
  (response) => {
    // Skip transformation for binary responses (Blob, ArrayBuffer)
    if (response.data instanceof Blob || response.data instanceof ArrayBuffer) {
      return response
    }
    if (response.data && typeof response.data === 'object') {
      // Handle arrays at top level
      if (Array.isArray(response.data)) {
        response.data = response.data.map((item: Record<string, unknown>) =>
          item && typeof item === 'object' ? snakeToCamelCase(item) : item
        )
      } else {
        response.data = snakeToCamelCase(response.data)
      }
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Handle 401 errors - token expired
    if (error.response?.status === 401 && !originalRequest._retry && tokenRefreshCallback) {
      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          return apiClient(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const newToken = await tokenRefreshCallback()
        if (newToken) {
          setAuthToken(newToken)
          processQueue(null, newToken)
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          return apiClient(originalRequest)
        } else {
          processQueue(new Error('Failed to refresh token'))
          return Promise.reject(new Error('Session expired. Please log in again.'))
        }
      } catch (refreshError) {
        processQueue(refreshError as Error)
        return Promise.reject(new Error('Session expired. Please log in again.'))
      } finally {
        isRefreshing = false
      }
    }

    // Handle other API errors consistently
    if (error.response) {
      let message = error.response.data?.detail || error.response.data?.message || ''
      // Handle case where detail/message is an object
      if (typeof message === 'object') {
        message = JSON.stringify(message)
      }
      // Include status code so callers can match on it (e.g. 502, 504)
      const status = error.response.status
      if (!message) {
        message = `Request failed with status ${status}`
      }
      const err = new Error(String(message))
      ;(err as Error & { status?: number }).status = status
      return Promise.reject(err)
    }
    if (error.request) {
      // Network error - always treat as offline/network unavailable
      // Use empty message so it won't show in toasts
      const offlineError = new Error('')
      offlineError.name = 'OfflineError'
      return Promise.reject(offlineError)
    }
    return Promise.reject(error)
  }
)

/**
 * Set the auth token for API requests
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete apiClient.defaults.headers.common['Authorization']
  }
}

/**
 * API request with AbortController support
 */
export async function apiRequest<T>(
  config: AxiosRequestConfig & { signal?: AbortSignal }
): Promise<T> {
  const response = await apiClient.request<T>(config)
  return response.data
}

/**
 * GET request helper
 */
export async function get<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.get<T>(url, config)
  return response.data
}

/**
 * POST request helper
 */
export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config)
  return response.data
}

/**
 * PUT request helper
 */
export async function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config)
  return response.data
}

/**
 * DELETE request helper
 */
export async function del<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.delete<T>(url, config)
  return response.data
}

/**
 * PATCH request helper
 */
export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config)
  return response.data
}

/**
 * Check if an error is from a cancelled/aborted request
 */
export function isRequestCancelled(error: unknown): boolean {
  if (error instanceof Error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') return true
    if (error.message === 'canceled') return true
  }
  return false
}

/**
 * Check if error is an offline/network error (should be handled silently)
 */
export function isOfflineError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'OfflineError' ||
           error.message === '' ||
           error.message === 'Network Error' ||
           error.message.includes('ERR_NETWORK')
  }
  return false
}

export default apiClient
